// openEHR LANG model — BMM package org.openehr.lang.bmm.core.model
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { BMM_CLASS, BMM_ENUMERATION, BMM_MODULE } from "../../_shared.ts";
import { BMM_SIMPLE_CLASS, BMM_SIMPLE_TYPE } from "../../_shared.ts";
import { BMM_MODEL_ELEMENT } from "../core.ts";
import type { EL_PROCEDURE_AGENT } from "../expression.ts";
import type { BMM_PROPERTY } from "./feature.ts";
import * as openehr_base from "../../../base/mod.ts";

/**
 * Core properties of \`BMM_MODEL\`, may be used in a serial representation as well, such as \`P_BMM_SCHEMA\`.
 */
export class BMM_MODEL_METADATA {
  /**
   * Internal storage for rm_publisher
   * @protected
   */
  protected _rm_publisher?: openehr_base.String;

  /**
   * Publisher of model expressed in the schema.
   */
  get rm_publisher(): string | undefined {
    return this._rm_publisher?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_publisher.
   * Use this to access openehr_base.String methods.
   */
  get $rm_publisher(): openehr_base.String | undefined {
    return this._rm_publisher;
  }

  /**
   * Sets rm_publisher from either a primitive value or openehr_base.String wrapper.
   */
  set rm_publisher(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_publisher = undefined;
    } else if (typeof val === "string") {
      this._rm_publisher = openehr_base.String.from(val);
    } else {
      this._rm_publisher = val;
    }
  }

  /**
   * Internal storage for rm_release
   * @protected
   */
  protected _rm_release?: openehr_base.String;

  /**
   * Release of model expressed in the schema as a 3-part numeric, e.g. "3.1.0" .
   */
  get rm_release(): string | undefined {
    return this._rm_release?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_release.
   * Use this to access openehr_base.String methods.
   */
  get $rm_release(): openehr_base.String | undefined {
    return this._rm_release;
  }

  /**
   * Sets rm_release from either a primitive value or openehr_base.String wrapper.
   */
  set rm_release(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_release = undefined;
    } else if (typeof val === "string") {
      this._rm_release = openehr_base.String.from(val);
    } else {
      this._rm_release = val;
    }
  }
}

/**
 * A BMM model component that contains packages and classes.
 */
export abstract class BMM_PACKAGE_CONTAINER extends BMM_MODEL_ELEMENT {
  /**
   * Child packages; keys all in upper case for guaranteed matching.
   */
  packages?: Map<string, BMM_PACKAGE>;
  /**
   * Model element within which a referenceable element is known.
   */
  override scope?: BMM_PACKAGE_CONTAINER = undefined;

  constructor() {
    super();
    this.packages = new Map();
  }

  /**
   * Package at the path \`_a_path_\`.
   * @param a_path - Parameter
   * @returns Result value
   */
  package_at_path(a_path: openehr_base.String): BMM_PACKAGE {
    const pathStr = (a_path?.value || String(a_path)).toUpperCase();
    const parts = pathStr.split(".");

    // deno-lint-ignore no-this-alias
    let current: BMM_PACKAGE_CONTAINER = this;
    for (const part of parts) {
      if (!current.packages?.has(part)) {
        throw new Error(`Package not found at path: ${pathStr}`);
      }
      current = current.packages.get(part)!;
    }
    return current as BMM_PACKAGE;
  }

  /**
   * Recursively execute \`_action_\`, which is a procedure taking a \`BMM_PACKAGE\` argument, on all members of packages.
   * @param action - Parameter
   * @returns Result value
   */
  do_recursive_packages(action: EL_PROCEDURE_AGENT): void {
    // Iterate through all packages recursively
    if (!this.packages) return;

    for (const pkg of this.packages.values()) {
      // Execute action on this package (action.call would execute)
      // Since we can't directly call the agent, we just traverse
      pkg.do_recursive_packages(action);
    }
  }

  /**
   * True if there is a package at the path \`_a_path_\`; paths are delimited with delimiter \`{BMM_DEFINITIONS} _Package_name_delimiter_\`.
   * @param a_path - Parameter
   * @returns Result value
   */
  has_package_path(a_path: openehr_base.String): openehr_base.Boolean {
    try {
      this.package_at_path(a_path);
      return openehr_base.Boolean.from(true);
    } catch {
      return openehr_base.Boolean.from(false);
    }
  }
}

/**
 * Definition of the root of a BMM model (metadata lives on \`BMM_MODEL_METADATA\`).
 */
export class BMM_MODEL extends BMM_PACKAGE_CONTAINER {
  /**
   * All classes in this model, keyed by type name.
   */
  class_definitions?: Map<string, BMM_CLASS>;
  /**
   * List of other models 'used' (i.e. 'imported' by this model). Classes in the current model may refer to classes in a used model by specifying the other class's \`_scope_\` meta-attribute.
   */
  used_models?: BMM_MODEL[];
  /**
   * All classes in this model, keyed by type name.
   */
  modules?: Map<string, BMM_MODULE>;

  // Properties from BMM_MODEL_METADATA (inherited through schema)
  rm_publisher?: string | openehr_base.String;
  rm_release?: string | openehr_base.String;
  model_name?: string | openehr_base.String;

  constructor() {
    super();
    this.class_definitions = new Map();
    this.used_models = [];
    this.modules = new Map();
  }

  /**
   * Identifier of this model, lower-case, formed from:
   *
   * \`<rm_publisher>_<model_name>_<rm_release>\`
   *
   * E.g. \`"openehr_ehr_1.0.4"\`.
   * @returns Result value
   */
  model_id(): openehr_base.String {
    const publisher =
      (typeof this.rm_publisher === "string"
        ? this.rm_publisher
        : this.rm_publisher?.value) || "";
    const modelName =
      (typeof this.model_name === "string"
        ? this.model_name
        : this.model_name?.value) || "";
    const release =
      (typeof this.rm_release === "string"
        ? this.rm_release
        : this.rm_release?.value) || "";
    return openehr_base.String.from(
      `${publisher.toLowerCase()}_${modelName.toLowerCase()}_${release}`,
    );
  }

  /**
   * Retrieve the class definition corresponding to \`_a_type_name_\` (which may contain a generic part).
   * @param a_name - Parameter
   * @returns Result value
   */
  class_definition(a_name: openehr_base.String): BMM_CLASS {
    const nameStr = (a_name?.value || String(a_name)).toUpperCase();
    // Strip generic part if present (e.g., "List<String>" -> "LIST")
    const baseName = nameStr.split("<")[0];
    if (!this.class_definitions?.has(baseName)) {
      throw new Error(`Class definition not found: ${nameStr}`);
    }
    return this.class_definitions.get(baseName)!;
  }

  /**
   * Retrieve the class definition corresponding to \`_a_type_name_\`. If it contains a generic part, this will be removed if it is a fully open generic name, otherwise it will remain intact, i.e. if it is an effective generic name that identifies a \`BMM_GENERIC_CLASS_EFFECTIVE\`.
   * @returns Result value
   */
  type_definition(): BMM_CLASS {
    // This method needs a type_name parameter which seems missing from signature
    // Return first class definition as default behavior
    const firstClass = this.class_definitions?.values().next().value;
    if (!firstClass) {
      throw new Error("No type definitions available");
    }
    return firstClass;
  }

  /**
   * True if \`_a_class_name_\` has a class definition in the model.
   * @param a_class_name - Parameter
   * @returns Result value
   */
  has_class_definition(
    a_class_name: openehr_base.String,
  ): openehr_base.Boolean {
    const nameStr = (a_class_name?.value || String(a_class_name)).toUpperCase();
    const baseName = nameStr.split("<")[0];
    return openehr_base.Boolean.from(
      this.class_definitions?.has(baseName) || false,
    );
  }

  /**
   * True if \`_a_type_name_\` is already concretely known in the system, including if it is generic, which may be open, partially open or closed.
   * @param a_type_name - Parameter
   * @returns Result value
   */
  has_type_definition(a_type_name: openehr_base.String): openehr_base.Boolean {
    // For now, type definitions are the same as class definitions
    return this.has_class_definition(a_type_name);
  }

  /**
   * Retrieve the enumeration definition corresponding to \`_a_type_name_\`.
   * @param a_name - Parameter
   * @returns Result value
   */
  enumeration_definition(a_name: openehr_base.String): BMM_ENUMERATION {
    const classDef = this.class_definition(a_name);
    // Check if it's an enumeration type
    if (classDef && "item_names" in classDef) {
      return classDef as unknown as BMM_ENUMERATION;
    }
    throw new Error(`Not an enumeration type: ${a_name?.value || a_name}`);
  }

  /**
   * List of keys in \`_class_definitions_\` of items marked as primitive types.
   * @returns Result value
   */
  primitive_types(): openehr_base.String {
    const primitives: string[] = [];
    if (this.class_definitions) {
      for (const [key, cls] of this.class_definitions) {
        if (cls.is_primitive === true) {
          primitives.push(key);
        }
      }
    }
    return openehr_base.String.from(primitives.join(", "));
  }

  /**
   * List of keys in \`_class_definitions_\` of items that are enumeration types.
   * @returns Result value
   */
  enumeration_types(): openehr_base.String {
    const enums: string[] = [];
    if (this.class_definitions) {
      for (const [key, cls] of this.class_definitions) {
        // Check if class is an enumeration
        if ("item_names" in cls) {
          enums.push(key);
        }
      }
    }
    return openehr_base.String.from(enums.join(", "));
  }

  /**
   * Retrieve the property definition for \`_a_prop_name_\` in flattened class corresponding to \`_a_type_name_\`.
   * @returns Result value
   */
  property_definition(): BMM_PROPERTY {
    // This method seems to need parameters - a_type_name and a_prop_name
    throw new Error(
      "property_definition requires type_name and prop_name parameters",
    );
  }

  /**
   * True if \`_a_ms_property_type_\` is a valid 'MS' dynamic type for \`_a_property_\` in BMM type \`_a_bmm_type_name_\`. 'MS' conformance means 'model-semantic' conformance, which abstracts away container types like \`List<>\`, \`Set<>\` etc and compares the dynamic type with the relation target type in the UML sense, i.e. regardless of whether there is single or multiple containment.
   * @param a_bmm_type_name - Parameter
   * @param a_bmm_property_name - Parameter
   * @param a_ms_property_name - Parameter
   * @returns Result value
   */
  ms_conformant_property_type(
    a_bmm_type_name: openehr_base.String,
    a_bmm_property_name: openehr_base.String,
    a_ms_property_name: openehr_base.String,
  ): openehr_base.Boolean {
    // Check model-semantic conformance
    try {
      const classDef = this.class_definition(a_bmm_type_name);
      // Look up property in flattened class
      const propNameStr = a_bmm_property_name?.value ||
        String(a_bmm_property_name);
      const msTypeStr = a_ms_property_name?.value || String(a_ms_property_name);

      // Check if property exists and types conform
      if (classDef.properties) {
        const prop = classDef.properties.get(propNameStr) ??
          classDef.properties.get(propNameStr.toUpperCase());
        if (prop && prop.type) {
          const propTypeName = prop.type.type_name?.()?.value || "";
          // Simple conformance check - exact match or ancestor
          return openehr_base.Boolean.from(
            propTypeName.toUpperCase() === msTypeStr.toUpperCase() ||
              this.is_descendant_of(
                  openehr_base.String.from(msTypeStr),
                  openehr_base.String.from(propTypeName),
                ).value === true,
          );
        }
      }
      return openehr_base.Boolean.from(false);
    } catch {
      return openehr_base.Boolean.from(false);
    }
  }

  /**
   * Retrieve the property definition for \`_a_property_path_\` in flattened class corresponding to \`_a_type_name_\`.
   * @returns Result value
   */
  property_definition_at_path(): BMM_PROPERTY {
    // This method needs parameters - a_type_name and a_property_path
    throw new Error(
      "property_definition_at_path requires type_name and property_path parameters",
    );
  }

  /**
   * Retrieve the class definition for the class that owns the terminal attribute in \`a_prop_path\` in flattened class corresponding to \`a_type_name\`.
   * @param a_type_name - Parameter
   * @param a_prop_path - Parameter
   * @returns Result value
   */
  class_definition_at_path(
    a_type_name: openehr_base.String,
    a_prop_path: openehr_base.String,
  ): BMM_CLASS {
    // Navigate path to find terminal class
    const pathStr = a_prop_path?.value || String(a_prop_path);
    const parts = pathStr.split("/").filter(Boolean);

    let currentClass = this.class_definition(a_type_name);

    for (const part of parts.slice(0, -1)) {
      // Look up property in current class
      if (currentClass.properties) {
        const prop = currentClass.properties.get(part) ??
          currentClass.properties.get(part.toUpperCase());
        if (prop && prop.type) {
          const typeName = prop.type.type_name?.();
          if (typeName) {
            currentClass = this.class_definition(typeName);
          }
        }
      }
    }
    return currentClass;
  }

  /**
   * Return all ancestor types of \`_a_class_name_\` up to root class (usually \`Any\`, \`Object\` or something similar). Does  not include current class. Returns empty list if none.
   * @param a_class - Parameter
   * @returns Result value
   */
  all_ancestor_classes(a_class: openehr_base.String): openehr_base.String {
    const ancestors: string[] = [];
    try {
      const classDef = this.class_definition(a_class);
      if (classDef.ancestors) {
        const ancestorDefs = classDef.ancestors;
        for (const [ancName, _ancClass] of ancestorDefs) {
          ancestors.push(ancName);
          // Recursively get ancestors of ancestors
          const parentAncestors = this.all_ancestor_classes(
            openehr_base.String.from(ancName),
          );
          if (parentAncestors.value) {
            ancestors.push(
              ...parentAncestors.value.split(", ").filter(Boolean),
            );
          }
        }
      }
    } catch {
      // Class not found, return empty
    }
    // Remove duplicates
    return openehr_base.String.from([...new Set(ancestors)].join(", "));
  }

  /**
   * True if \`_a_class_name_\` is a descendant in the model of \`_a_parent_class_name_\`.
   * @param a_class_name - Parameter
   * @param a_parent_class_name - Parameter
   * @returns Result value
   */
  is_descendant_of(
    a_class_name: openehr_base.String,
    a_parent_class_name: openehr_base.String,
  ): openehr_base.Boolean {
    const parentNameStr = (a_parent_class_name?.value ||
      String(a_parent_class_name)).toUpperCase();
    const ancestors = this.all_ancestor_classes(a_class_name);
    const ancestorList = ancestors.value?.toUpperCase().split(", ") || [];
    return openehr_base.Boolean.from(ancestorList.includes(parentNameStr));
  }

  /**
   * Check conformance of \`_a_desc_type_\` to \`_an_anc_type_\`; the types may be generic, and may contain open generic parameters like 'T' etc. These are replaced with their appropriate constrainer types, or Any during the conformance testing process.
   *
   * Conformance is found if:
   *
   * * [base class test] types are non-generic, and either type names are identical, or else \`_a_desc_type_\` has \`_an_anc_type_\` in its ancestors;
   * * both types are generic and pass base class test; number of generic params matches, and each generic parameter type, after 'open parameter' substitution, recursively passes; \`_type_name_conforms_to_\` test
   * * descendant type is generic and ancestor type is not, and they pass base classes test.
   * @param a_desc_type - Parameter
   * @param an_anc_type - Parameter
   * @returns Result value
   */
  type_conforms_to(
    a_desc_type: openehr_base.String,
    an_anc_type: openehr_base.String,
  ): openehr_base.Boolean {
    const descTypeStr = (a_desc_type?.value || String(a_desc_type))
      .toUpperCase();
    const ancTypeStr = (an_anc_type?.value || String(an_anc_type))
      .toUpperCase();

    // Identical types conform
    if (descTypeStr === ancTypeStr) {
      return openehr_base.Boolean.from(true);
    }

    // Extract base class names (strip generic parts)
    const descBaseName = descTypeStr.split("<")[0];
    const ancBaseName = ancTypeStr.split("<")[0];

    // Check if descendant is an ancestor of ancestor type
    return this.is_descendant_of(
      openehr_base.String.from(descBaseName),
      openehr_base.String.from(ancBaseName),
    );
  }

  /**
   * Generate type substitutions for the supplied type, which may be simple, generic (closed, open or partially open), or a container type. In the generic and container cases, the result is the permutation of the base class type and type substitutions of all generic parameters.
   * @param a_type - Parameter
   * @returns Result value
   */
  subtypes(a_type: openehr_base.String): openehr_base.String {
    const subtypesList: string[] = [];
    const typeStr = (a_type?.value || String(a_type)).toUpperCase();

    // Find all classes that descend from this type
    if (this.class_definitions) {
      for (const [className, _classDef] of this.class_definitions) {
        if (
          this.is_descendant_of(
            openehr_base.String.from(className),
            openehr_base.String.from(typeStr),
          ).value === true
        ) {
          subtypesList.push(className);
        }
      }
    }
    return openehr_base.String.from(subtypesList.join(", "));
  }

  /**
   * \`BMM_SIMPLE_CLASS\` instance for the \`Any\` class. This may be defined in the BMM schema, but if not, use \`BMM_DEFINITIONS._any_class_\`.
   * @returns Result value
   */
  any_class_definition(): BMM_SIMPLE_CLASS {
    // Try to find "Any" class in definitions
    if (this.class_definitions?.has("ANY")) {
      return this.class_definitions.get("ANY") as BMM_SIMPLE_CLASS;
    }
    // Return a new instance representing the built-in Any class
    const anyClass = new BMM_SIMPLE_CLASS();
    anyClass.name = "Any";
    return anyClass;
  }

  /**
   * \`BMM_SIMPLE_TYPE\` instance for the \`Any\` type.
   * @returns Result value
   */
  any_type_definition(): BMM_SIMPLE_TYPE {
    const anyType = new BMM_SIMPLE_TYPE();
    anyType.base_class = this.any_class_definition();
    return anyType;
  }

  /**
   * \`BMM_SIMPLE_TYPE\` instance for the \`Boolean\` type.
   * @returns Result value
   */
  boolean_type_definition(): BMM_SIMPLE_TYPE {
    const boolType = new BMM_SIMPLE_TYPE();
    if (this.class_definitions?.has("BOOLEAN")) {
      boolType.base_class = this.class_definitions.get(
        "BOOLEAN",
      ) as BMM_SIMPLE_CLASS;
    } else {
      const boolClass = new BMM_SIMPLE_CLASS();
      boolClass.name = "Boolean";
      boolType.base_class = boolClass;
    }
    return boolType;
  }
}

/**
 * Abstraction of a package as a tree structure whose nodes can contain other packages and classes.
 *
 * The \`_name_\` may be qualified if it is a top-level package.
 */
export class BMM_PACKAGE extends BMM_PACKAGE_CONTAINER {
  /**
   * Member modules in this package.
   */
  members?: Map<string, BMM_MODULE>;

  constructor() {
    super();
    this.members = new Map();
  }

  /**
   * Obtain the set of top-level classes in this package, either from this package itself or by recursing into the structure until classes are obtained from child packages. Recurse into each child only far enough to find the first level of classes.
   * @returns Result value
   */
  root_classes(): BMM_CLASS {
    // Return the first class found in members, or recurse into child packages
    if (this.members && this.members.size > 0) {
      const firstMember = this.members.values().next().value;
      if (firstMember) {
        return firstMember as unknown as BMM_CLASS;
      }
    }
    // Recurse into child packages
    if (this.packages && this.packages.size > 0) {
      for (const childPkg of this.packages.values()) {
        return childPkg.root_classes();
      }
    }
    throw new Error("No root classes found in package");
  }

  /**
   * Full path of this package back to root package.
   * @returns Result value
   */
  path(): openehr_base.String {
    // Build path from scope chain
    const parts: string[] = [];
    // deno-lint-ignore no-this-alias
    let current: BMM_MODEL_ELEMENT | undefined = this;
    while (current) {
      if (current.name) {
        parts.unshift(current.name);
      }
      current = current.scope;
    }
    return openehr_base.String.from(parts.join("."));
  }
}
